import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Play, Star, Award, Users, Wrench } from 'lucide-react';
import axios from 'axios';
import { HeroCarousel } from '../components/HeroCarousel';
import { useSiteContent } from '../context/SiteContentContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const services = [
  { icon: Play, title: 'Custom Theater Rooms', desc: 'Complete design and build of premium home theater rooms tailored to your space.' },
  { icon: Star, title: 'AV Systems', desc: 'Cutting-edge projectors, screens, surround sound, and smart control installations.' },
  { icon: Award, title: 'Concession Stands', desc: 'Custom-built concession areas with display, lighting, and mini bars.' },
  { icon: Wrench, title: 'Media Consoles', desc: 'Handcrafted media consoles with premium finishes and cable management.' },
];

export default function HomePage() {
  const [testimonials, setTestimonials] = useState([]);
  const { c } = useSiteContent();

  useEffect(() => {
    axios.get(`${API}/testimonials`).then(r => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        <HeroCarousel />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-32">
          <motion.div {...fadeUp} className="max-w-2xl">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-manrope font-semibold mb-6">
              {c('home.hero.eyebrow', 'Building the best theater rooms since 2013')}
            </p>
            <h1 className="font-playfair text-5xl lg:text-7xl text-white leading-[1.1] mb-6">
              {c('home.hero.title', 'Bring Your Home Theater')}{' '}
              <span className="text-[#D4AF37]">{c('home.hero.title_accent', 'Vision to Life')}</span>
            </h1>
            <p className="text-white/40 font-manrope text-lg leading-relaxed mb-10 max-w-lg">
              {c('home.hero.subtitle', 'We design and build premium home theater rooms. Every home deserves a room you can love.')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/book"
                className="inline-flex items-center gap-3 bg-[#D4AF37] text-black font-manrope font-bold text-sm uppercase tracking-widest px-8 py-4 rounded-sm hover:bg-[#C5A059] transition-all duration-300"
                data-testid="hero-book-btn"
              >
                Book a Consultation
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/design-your-theater"
                className="inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] font-manrope font-semibold text-sm uppercase tracking-widest px-8 py-4 rounded-sm hover:bg-[#D4AF37] hover:text-black transition-all duration-300"
                data-testid="hero-designer-btn"
              >
                Design Your Theater
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About / Stats */}
      <section className="py-24 border-t border-white/5" data-testid="about-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-manrope font-semibold mb-4">About Dream Room Theaters</p>
              <h2 className="font-playfair text-3xl lg:text-4xl text-white leading-tight mb-6">
                {c('home.about.title', 'Unmatched Craftsmanship & Superior Service')}
              </h2>
              <p className="text-white/40 font-manrope leading-relaxed mb-6">
                {c('home.about.body', 'At Dream Room Theaters, we take pride in delivering unmatched craftsmanship and superior service. We craft immersive, custom home theaters where luxury meets comfort. Designed for connection and privacy, our rooms offer space to gather, share memories, and enjoy peace.')}
              </p>
              <Link to="/packages" className="inline-flex items-center gap-2 text-[#D4AF37] font-manrope text-sm uppercase tracking-widest hover:gap-4 transition-all duration-300" data-testid="about-packages-link">
                View Our Packages <ArrowRight size={14} />
              </Link>
            </motion.div>
            <motion.div {...fadeUp} className="grid grid-cols-2 gap-4">
              {[
                { number: '12+', label: 'Years Experience' },
                { number: '100%', label: 'Client Satisfaction' },
              ].map((stat, i) => (
                <div key={i} className="bg-[#1a1a1a] border border-white/5 rounded-sm p-6 text-center hover:border-[#D4AF37]/20 transition-all duration-300">
                  <span className="font-playfair text-3xl text-[#D4AF37] block mb-1">{stat.number}</span>
                  <span className="text-white/40 text-xs font-manrope uppercase tracking-widest">{stat.label}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-[#0a0a0b]" data-testid="services-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} className="text-center mb-16">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-manrope font-semibold mb-4">What We Offer</p>
            <h2 className="font-playfair text-3xl lg:text-4xl text-white">Our Services</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((svc, i) => {
              const Icon = svc.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-[#111113] border border-white/5 rounded-sm p-8 hover:border-[#D4AF37]/30 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-sm bg-[#D4AF37]/10 flex items-center justify-center mb-5 group-hover:bg-[#D4AF37]/20 transition-colors duration-300">
                    <Icon size={20} className="text-[#D4AF37]" />
                  </div>
                  <h3 className="font-playfair text-lg text-white mb-2">{svc.title}</h3>
                  <p className="text-white/35 text-sm font-manrope leading-relaxed">{svc.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-24" data-testid="gallery-preview">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} className="flex items-end justify-between mb-12">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-manrope font-semibold mb-4">Our Work</p>
              <h2 className="font-playfair text-3xl lg:text-4xl text-white">Featured Projects</h2>
            </div>
            <Link to="/gallery" className="hidden sm:inline-flex items-center gap-2 text-[#D4AF37] font-manrope text-xs uppercase tracking-widest hover:gap-4 transition-all duration-300" data-testid="gallery-see-all">
              View All <ArrowRight size={14} />
            </Link>
          </motion.div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              c('home.featured.img1', 'https://static.prod-images.emergentagent.com/jobs/5673d3ec-9da0-4482-ba34-3bd63a96723f/images/c468737b7ff883bbdf387ef05b2bf92b3c8cf0c10898961cd57fe67e9365eb3a.jpeg'),
              c('home.featured.img2', 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=1200&q=80'),
              c('home.featured.img3', 'https://static.prod-images.emergentagent.com/jobs/5673d3ec-9da0-4482-ba34-3bd63a96723f/images/81db26e92a3eb9c293b6310fd3545b854f3d57f5c87407d3facfe116e1e641ef.jpeg'),
              c('home.featured.img4', 'https://static.prod-images.emergentagent.com/jobs/5673d3ec-9da0-4482-ba34-3bd63a96723f/images/73566705726732e72cae9c03d6e4d449f8a926878face09083015a012336b3fe.jpeg'),
            ].map((img, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="aspect-square overflow-hidden rounded-sm group"
              >
                <img src={img} alt={`Project ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </motion.div>
            ))}
          </div>
          <Link to="/gallery" className="sm:hidden inline-flex items-center gap-2 text-[#D4AF37] font-manrope text-xs uppercase tracking-widest mt-8">
            View All <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-24 bg-[#0a0a0b]" data-testid="testimonials-section">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <motion.div {...fadeUp} className="text-center mb-16">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-manrope font-semibold mb-4">Testimonials</p>
              <h2 className="font-playfair text-3xl lg:text-4xl text-white">What Our Clients Say</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-[#111113] border border-white/5 rounded-sm p-8"
                >
                  <div className="flex gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map(s => <Star key={s} size={14} className="text-[#D4AF37] fill-[#D4AF37]" />)}
                  </div>
                  <p className="text-white/60 font-manrope text-sm leading-relaxed mb-6 italic">"{t.quote}"</p>
                  <div>
                    <p className="text-white font-manrope font-semibold text-sm">{t.name}</p>
                    <p className="text-white/30 font-manrope text-xs">{t.location}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Multimedia */}
      <section className="py-24 bg-[#0a0a0b]" data-testid="multimedia-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} className="mb-12">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-manrope font-semibold mb-4">Multimedia</p>
            <h2 className="font-playfair text-3xl lg:text-4xl text-white">Step Inside the Experience</h2>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <motion.div {...fadeUp} className="lg:col-span-3 lg:col-start-2">
              <div className="relative aspect-video overflow-hidden rounded-sm border border-white/5 bg-black">
                <video
                  src="/media/showcase-cinema.mp4"
                  controls
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover"
                  data-testid="multimedia-video"
                />
              </div>
              <p className="text-white/30 text-xs font-manrope mt-4 text-center uppercase tracking-widest">Featured Reel — More Premieres Coming Soon</p>
            </motion.div>
            <motion.div {...fadeUp} className="lg:col-span-3 lg:col-start-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { src: c('home.multimedia.img1', 'https://static.prod-images.emergentagent.com/jobs/5673d3ec-9da0-4482-ba34-3bd63a96723f/images/ffd9b57c6181322c0a000c96e145d645d75bdf577349e9cda033988a1175e6af.jpeg'), label: c('home.multimedia.label1', 'Custom Concessions') },
                { src: c('home.multimedia.img2', 'https://static.prod-images.emergentagent.com/jobs/5673d3ec-9da0-4482-ba34-3bd63a96723f/images/918532743e88aae4041c8a5dfb15e0429c2affbc5a307e47e8f5d5dda401115a.jpeg'), label: c('home.multimedia.label2', 'Starlight Ceilings') },
              ].map((item, i) => (
                <Link key={i} to="/gallery" className="relative overflow-hidden rounded-sm border border-white/5 group aspect-[16/10]" data-testid={`multimedia-tile-${i}`}>
                  <img src={item.src} alt={item.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <span className="absolute bottom-4 left-4 text-white font-manrope text-xs uppercase tracking-widest">{item.label}</span>
                </Link>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" data-testid="cta-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#111113] border border-white/5 rounded-sm p-12 lg:p-16 text-center">
            <motion.div {...fadeUp}>
              <h2 className="font-playfair text-3xl lg:text-5xl text-white mb-4">
                {c('home.cta.title', 'Ready to Build Your')} <span className="text-[#D4AF37]">{c('home.cta.title_accent', 'Dream Theater?')}</span>
              </h2>
              <p className="text-white/40 font-manrope max-w-xl mx-auto mb-8">
                {c('home.cta.subtitle', 'Take the first step toward your premium home theater experience. Book a personalized consultation with our experts today.')}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/book"
                  className="inline-flex items-center gap-3 bg-[#D4AF37] text-black font-manrope font-bold text-sm uppercase tracking-widest px-8 py-4 rounded-sm hover:bg-[#C5A059] transition-all duration-300"
                  data-testid="cta-book-btn"
                >
                  Book Now <ArrowRight size={16} />
                </Link>
                <Link
                  to="/design-your-theater"
                  className="inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] font-manrope font-semibold text-sm uppercase tracking-widest px-8 py-4 rounded-sm hover:bg-[#D4AF37] hover:text-black transition-all duration-300"
                  data-testid="cta-designer-btn"
                >
                  Try Room Designer
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Crypto Notice */}
      <section className="py-8 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <p className="text-white/20 font-manrope text-sm">
            <span className="text-[#D4AF37]">Dream Room Theaters</span> now accepts cryptocurrency payments
          </p>
        </div>
      </section>
    </div>
  );
}
